import { format, formatDistanceToNow, parseISO } from 'date-fns';

const inrFormatter = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0,
});

const compactNumberFormatter = new Intl.NumberFormat('en-IN', {
  notation: 'compact',
  maximumFractionDigits: 1,
});

const numberFormatter = new Intl.NumberFormat('en-IN');

/**
 * Per-currency formatter cache. `Intl.NumberFormat` is cheap but not
 * free to construct; we memoise per (locale, currency) pair so repeated
 * `fmt.money` calls don't re-create the formatter every render.
 */
const moneyFormatters = new Map<string, Intl.NumberFormat>();
function moneyFormatter(currency: string, locale: string): Intl.NumberFormat {
  const key = `${locale}|${currency}`;
  let f = moneyFormatters.get(key);
  if (!f) {
    f = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      // Hide fraction digits at zero so 1000 renders as ₹1,000 not ₹1,000.00 —
      // matches the existing fmt.inr behaviour. Two digits when there's a
      // fractional component (taxes, exchange rates).
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
    });
    moneyFormatters.set(key, f);
  }
  return f;
}

/**
 * Choose a sensible BCP-47 locale per currency for grouping/separator
 * conventions. Indian numbering (`1,00,000`) only kicks in for INR; most
 * others use the en-US convention (`100,000`). Add overrides as needed.
 */
function localeForCurrency(currency: string): string {
  switch (currency) {
    case 'INR': return 'en-IN';
    case 'USD':
    case 'CAD':
    case 'AUD':
    case 'SGD':
    case 'HKD': return 'en-US';
    case 'GBP': return 'en-GB';
    case 'EUR': return 'en-IE'; // Generic English-EU
    case 'AED': return 'en-AE';
    case 'SAR': return 'en-SA';
    default:    return 'en-US';
  }
}

export const fmt = {
  /** @deprecated Prefer `fmt.money(value, currency)` so non-INR tenants render correctly. */
  inr(value: number | null | undefined) {
    if (value == null || Number.isNaN(value)) return '—';
    return inrFormatter.format(value);
  },
  /**
   * Currency-aware money formatter. Pass the ISO 4217 currency code from
   * the row (`order.currency`, `invoice.currency`, etc.) — never assume
   * INR. For tenant-default rendering (KPIs, totals) read from
   * `useTenantLocale().currency`.
   */
  money(value: number | null | undefined, currency: string = 'INR') {
    if (value == null || Number.isNaN(value)) return '—';
    return moneyFormatter(currency, localeForCurrency(currency)).format(value);
  },
  number(value: number | null | undefined) {
    if (value == null || Number.isNaN(value)) return '—';
    return numberFormatter.format(value);
  },
  compact(value: number | null | undefined) {
    if (value == null || Number.isNaN(value)) return '—';
    return compactNumberFormatter.format(value);
  },
  /**
   * Default date format is `dd/MMM/yyyy` (e.g. `15/May/2026`). The worldwide
   * product uses month abbreviations to avoid the dd/mm vs mm/dd ambiguity
   * that pure-numeric formats have across locales.
   */
  date(value: string | Date | null | undefined, pattern = 'dd/MMM/yyyy') {
    if (!value) return '—';
    const d = typeof value === 'string' ? parseISO(value) : value;
    if (Number.isNaN(d.getTime())) return '—';
    return format(d, pattern);
  },
  /**
   * Default date-time format is `dd/MMM/yyyy HH:mm:ss` — full seconds
   * resolution so operators can correlate timeline events.
   */
  dateTime(value: string | Date | null | undefined) {
    return fmt.date(value, 'dd/MMM/yyyy HH:mm:ss');
  },
  relative(value: string | Date | null | undefined) {
    if (!value) return '—';
    const d = typeof value === 'string' ? parseISO(value) : value;
    if (Number.isNaN(d.getTime())) return '—';
    return formatDistanceToNow(d, { addSuffix: true });
  },
  percent(value: number | null | undefined, fractionDigits = 0) {
    if (value == null || Number.isNaN(value)) return '—';
    return `${value.toFixed(fractionDigits)}%`;
  },
};
